import { useEffect, useState, useRef, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ProgressView } from '@/components/transcription/ProgressView';
import { computeDisplayProgress } from '@/lib/transcriptionProgress';
import { ResultView } from '@/components/transcription/ResultView';
import { apiFetch, API_BASE } from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import { useTranscriptionJobs, TranscriptionJob } from '@/contexts/TranscriptionContext';

// ── Types ──

type JobStatus = 'queued' | 'processing' | 'completed' | 'failed' | 'not_found';

interface ApiSpeaker {
  id: string;
  name?: string;
  display_name?: string;
  role?: string;
  bio?: string;
  confidence?: number;
}

interface ApiSegment {
  speaker: string;
  text: string;
  start_ms: number;
  end_ms: number;
}

interface Segment {
  speaker: string;
  text: string;
  timestamp?: string;
  start_ms?: number;
  end_ms?: number;
}

interface JobResult {
  title?: string;
  channel_name?: string;
  summary?: string;
  speakers?: ApiSpeaker[];
  segments?: ApiSegment[];
  duration_ms?: number;
  minutes_used?: number;
  item_id?: string;
  saved_item_id?: string;
  id?: string;
  url?: string;
  video_id?: string;
}

interface JobResponse {
  job_id: string;
  status: JobStatus;
  progress?: number;
  message?: string;
  result?: JobResult;
  error?: string | { message?: string; error_type?: string };
  error_type?: string;
  item_id?: string;
}

// Store completed job results for expanded view
interface CompletedJobData {
  title: string;
  channelName: string;
  summary: string;
  segments: Segment[];
  speakers: ApiSpeaker[];
  durationMs: number;
  minutesUsed?: number;
  itemId?: string;
  url?: string;
  videoId?: string;
}

// ── Helpers ──

function msToTimestamp(ms: number): string {
  const totalSeconds = Math.floor(ms / 1000);
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
}

function msToDuration(ms: number): string {
  const totalSeconds = Math.floor(ms / 1000);
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  if (hours > 0) return `${hours}h ${minutes}m`;
  return `${minutes}m`;
}

function ensureSegmentsArray(raw: unknown): ApiSegment[] {
  if (Array.isArray(raw)) return raw;
  if (raw && typeof raw === 'object') {
    const obj = raw as Record<string, unknown>;
    if (Array.isArray(obj.segments)) return obj.segments;
    return Object.values(obj).filter(
      (v: unknown) => v && typeof v === 'object' && 'text' in (v as Record<string, unknown>)
    ) as ApiSegment[];
  }
  return [];
}
// ══════════════════════════════════════════════════════════════════════
// MULTI-JOB VIEW (when no jobId param)
// ══════════════════════════════════════════════════════════════════════

function MultiJobView() {
  const navigate = useNavigate();
  const { jobs } = useTranscriptionJobs();
  const [expandedJobs, setExpandedJobs] = useState<Set<string>>(new Set());
  const [jobResults, setJobResults] = useState<Record<string, CompletedJobData>>({});
  const [copiedJobs, setCopiedJobs] = useState<Set<string>>(new Set());

  // All jobs (hidden flag is only for bottom bar, not this page)
  // Sort: in-progress first, then completed, most recent first within each group
  const sortedJobs = [...jobs].sort((a, b) => {
    const aActive = a.status === 'polling';
    const bActive = b.status === 'polling';
    if (aActive !== bActive) return aActive ? -1 : 1;
    return b.addedAt - a.addedAt;
  });

  const activeCount = sortedJobs.filter((j) => j.status === 'polling').length;
  const completedCount = sortedJobs.filter((j) => j.status === 'completed').length;

  // Fetch result data for a completed job
  const fetchJobResult = useCallback(async (jobId: string, resultItemId?: string) => {
    if (jobResults[jobId]) return; // Already have it

    const token = localStorage.getItem('tempo_token');
    const headers = { ...(token ? { Authorization: `Bearer ${token}` } : {}) };

    // If we have a library item ID, fetch from library (more reliable)
    if (resultItemId) {
      try {
        const res = await fetch(`${API_BASE}/library/items/${resultItemId}`, { headers });
        if (res.ok) {
          const item = await res.json();
          const speakerNames: Record<string, string> = {};
          (item.speakers || []).forEach((s: ApiSpeaker) => {
            const name = s.display_name || s.name;
            if (name) speakerNames[s.id] = name;
          });

          const rawSegments = ensureSegmentsArray(item.transcript);
          const displaySegments: Segment[] = rawSegments.map((seg) => ({
            speaker: speakerNames[seg.speaker] || seg.speaker,
            text: seg.text,
            timestamp: msToTimestamp(seg.start_ms),
            start_ms: seg.start_ms,
            end_ms: seg.end_ms,
          }));

          const lastSeg = rawSegments[rawSegments.length - 1];
          const duration = item.duration_ms || lastSeg?.end_ms || 0;

          setJobResults((prev) => ({
            ...prev,
            [jobId]: {
              title: item.title || 'Transcript',
              channelName: item.channel_name || '',
              summary: item.summary || '',
              segments: displaySegments,
              speakers: item.speakers || [],
              durationMs: duration,
              minutesUsed: item.minutes_used,
              itemId: resultItemId,
              url: item.url,
              videoId: item.video_id,
            },
          }));
          return;
        }
      } catch (err) {
        console.error('Failed to fetch library item:', err);
      }
    }

    // Fallback: try job endpoint (for in-memory jobs)
    try {
      const res = await fetch(`${API_BASE}/job/${jobId}`, { headers });
      const data: JobResponse = await res.json();

      if (data.status === 'completed' && data.result) {
        const result = data.result;
        const speakerNames: Record<string, string> = {};
        (result.speakers || []).forEach((s) => {
          const name = s.display_name || s.name;
          if (name) speakerNames[s.id] = name;
        });

        const rawSegments = ensureSegmentsArray(result.segments);
        const displaySegments: Segment[] = rawSegments.map((seg) => ({
          speaker: speakerNames[seg.speaker] || seg.speaker,
          text: seg.text,
          timestamp: msToTimestamp(seg.start_ms),
          start_ms: seg.start_ms,
          end_ms: seg.end_ms,
        }));

        const lastSeg = rawSegments[rawSegments.length - 1];
        const duration = result.duration_ms || lastSeg?.end_ms || 0;

        setJobResults((prev) => ({
          ...prev,
          [jobId]: {
            title: result.title || 'Transcript',
            channelName: result.channel_name || '',
            summary: result.summary || '',
            segments: displaySegments,
            speakers: result.speakers || [],
            durationMs: duration,
            minutesUsed: result.minutes_used,
            itemId: result.item_id || result.saved_item_id || result.id || data.item_id,
            url: result.url,
            videoId: result.video_id,
          },
        }));
        return;
      }

      // Job not found or no result — mark as unavailable
      if (data.status === 'not_found' || !data.result) {
        setJobResults((prev) => ({
          ...prev,
          [jobId]: {
            title: 'Transcript unavailable',
            channelName: '',
            summary: 'This transcript has expired from memory and was not saved to your library. Guest transcriptions are temporary.',
            segments: [],
            speakers: [],
            durationMs: 0,
          },
        }));
      }
    } catch (err) {
      console.error('Failed to fetch job result:', err);
      // Mark as unavailable on error
      setJobResults((prev) => ({
        ...prev,
        [jobId]: {
          title: 'Transcript unavailable',
          channelName: '',
          summary: 'Failed to load transcript. Please try again.',
          segments: [],
          speakers: [],
          durationMs: 0,
        },
      }));
    }
  }, [jobResults]);

  const toggleExpand = (jobId: string, job: TranscriptionJob) => {
    setExpandedJobs((prev) => {
      const next = new Set(prev);
      if (next.has(jobId)) {
        next.delete(jobId);
      } else {
        next.add(jobId);
        // Fetch result data if completed
        if (job.status === 'completed') {
          fetchJobResult(jobId, job.resultItemId);
        }
      }
      return next;
    });
  };

  const buildMarkdown = (data: CompletedJobData): string => {
    const speakers = [...new Set(data.segments.map((s) => s.speaker))];
    let md = `# ${data.title}\n\n`;
    if (data.summary) md += `## Summary\n${data.summary}\n\n`;
    md += `## Speakers\n`;
    for (const name of speakers) md += `- ${name}\n`;
    md += `\n## Transcript\n\n`;
    for (const seg of data.segments) {
      const ts = seg.timestamp ? ` (${seg.timestamp})` : '';
      md += `**${seg.speaker}**${ts}\n${seg.text}\n\n`;
    }
    return md.trimEnd() + '\n';
  };

  const handleCopy = (jobId: string, data: CompletedJobData) => {
    const md = buildMarkdown(data);
    navigator.clipboard.writeText(md).then(() => {
      setCopiedJobs((prev) => new Set(prev).add(jobId));
      setTimeout(() => setCopiedJobs((prev) => {
        const next = new Set(prev);
        next.delete(jobId);
        return next;
      }), 2000);
    });
  };

  const handleDownload = (data: CompletedJobData) => {
    const md = buildMarkdown(data);
    const blob = new Blob([md], { type: 'text/markdown' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${data.title.replace(/[^a-z0-9]/gi, '_').substring(0, 50)}.md`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  // If no jobs, redirect to home
  if (sortedJobs.length === 0) {
    return (
      <div className="min-h-screen bg-white">
        <div className="mx-auto max-w-2xl px-4 py-16 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="space-y-4"
          >
            <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-gray-100">
              <svg className="h-8 w-8 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
            </div>
            <h1 className="text-xl font-semibold text-gray-900">No active transcriptions</h1>
            <p className="text-sm text-gray-500">
              Start a new transcription from the home page or browse podcasts.
            </p>
            <div className="flex justify-center gap-3 pt-4">
              <Link
                to="/library"
                className="rounded-md border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
              >
                View Library
              </Link>
              <Link
                to="/"
                className="rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700"
              >
                New Transcription
              </Link>
            </div>
          </motion.div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      <div className="mx-auto max-w-4xl px-4 py-8">
        {/* Header */}
        <div className="mb-6">
          <Link
            to="/"
            className="inline-flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700 mb-4"
          >
            <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
            </svg>
            Back
          </Link>
          <h1 className="text-2xl font-bold text-gray-900">Transcriptions</h1>
          <p className="text-sm text-gray-500 mt-1">
            {activeCount > 0 && `${activeCount} in progress`}
            {activeCount > 0 && completedCount > 0 && ' · '}
            {completedCount > 0 && `${completedCount} completed`}
          </p>
        </div>

        {/* Job List */}
        <div className="space-y-3">
          <AnimatePresence mode="popLayout">
            {sortedJobs.map((job) => {
              const isExpanded = expandedJobs.has(job.jobId);
              const result = jobResults[job.jobId];
              const isCompleted = job.status === 'completed';
              const isFailed = job.status === 'failed';
              const isPolling = job.status === 'polling';

              return (
                <motion.div
                  key={job.jobId}
                  layout
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, scale: 0.95 }}
                  className="rounded-lg border border-gray-200 bg-white overflow-hidden"
                >
                  {/* Job Header (always visible) */}
                  <button
                    onClick={() => isCompleted ? toggleExpand(job.jobId, job) : navigate(`/transcription/${job.jobId}`)}
                    className={`w-full flex items-center gap-3 px-4 py-3 text-left transition-colors ${
                      isCompleted ? 'hover:bg-gray-50 cursor-pointer' : ''
                    }`}
                  >
                    {/* Status Icon */}
                    {isCompleted && (
                      <div className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-green-100">
                        <svg className="h-3.5 w-3.5 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                        </svg>
                      </div>
                    )}
                    {isFailed && (
                      <div className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-red-100">
                        <svg className="h-3.5 w-3.5 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </div>
                    )}
                    {isPolling && (
                      <div className="h-3 w-3 shrink-0 animate-pulse rounded-full bg-blue-500" />
                    )}

                    {/* Title & Status */}
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium text-gray-900 truncate">{job.title}</p>
                      <p className={`text-xs ${isFailed ? 'text-red-500' : 'text-gray-500'}`}>
                        {isCompleted ? 'Completed — click to expand' : isFailed ? (job.error || 'Failed') : job.stage}
                      </p>
                    </div>

                    {/* Progress (if polling) */}
                    {isPolling && (() => {
                      const pct = computeDisplayProgress(job.startedAt, job.estimatedDurationSec, job.stage);
                      return (
                        <div className="flex items-center gap-2 shrink-0">
                          <div className="h-1.5 w-20 overflow-hidden rounded-full bg-gray-100">
                            <div
                              className="h-full rounded-full bg-blue-500 transition-all duration-500"
                              style={{ width: `${pct}%` }}
                            />
                          </div>
                          <span className="text-xs tabular-nums text-gray-400 w-8">{pct}%</span>
                        </div>
                      );
                    })()}

                    {/* Expand chevron (if completed) */}
                    {isCompleted && (
                      <svg
                        className={`h-5 w-5 shrink-0 text-gray-400 transition-transform ${isExpanded ? 'rotate-180' : ''}`}
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                        strokeWidth={2}
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                      </svg>
                    )}
                  </button>

                  {/* Expanded Content (completed jobs only) */}
                  <AnimatePresence>
                    {isExpanded && isCompleted && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.2 }}
                        className="overflow-hidden"
                      >
                        <div className="border-t border-gray-100 px-4 py-4 bg-gray-50">
                          {!result ? (
                            <div className="flex items-center justify-center py-8">
                              <div className="h-6 w-6 animate-spin rounded-full border-2 border-gray-200 border-t-blue-600" />
                              <span className="ml-2 text-sm text-gray-500">Loading transcript...</span>
                            </div>
                          ) : (
                            <div className="space-y-4">
                              {/* Metadata */}
                              <div className="flex flex-wrap items-center gap-3 text-xs text-gray-500">
                                {result.channelName && (
                                  <>
                                    <span className="font-medium text-gray-600">{result.channelName}</span>
                                    <span>•</span>
                                  </>
                                )}
                                {result.videoId && !result.videoId.startsWith('rss_') && (
                                  <>
                                    <a
                                      href={result.url || `https://www.youtube.com/watch?v=${result.videoId}`}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="inline-flex items-center text-red-500 hover:text-red-600 transition-colors"
                                      title="Watch on YouTube"
                                      onClick={(e) => e.stopPropagation()}
                                    >
                                      <svg
                                        xmlns="http://www.w3.org/2000/svg"
                                        viewBox="0 0 24 24"
                                        fill="currentColor"
                                        className="h-3.5 w-3.5"
                                      >
                                        <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z" />
                                      </svg>
                                    </a>
                                    <span>•</span>
                                  </>
                                )}
                                <span>{msToDuration(result.durationMs)}</span>
                                <span>•</span>
                                <span>{result.segments.length} segments</span>
                                <span>•</span>
                                <span>{new Set(result.segments.map(s => s.speaker)).size} speakers</span>
                                {result.minutesUsed !== undefined && (
                                  <>
                                    <span>•</span>
                                    <span>{result.minutesUsed} min used</span>
                                  </>
                                )}
                              </div>

                              {/* Summary */}
                              {result.summary && (
                                <div className="rounded-md bg-white p-3 border border-gray-200">
                                  <h4 className="text-xs font-medium text-gray-700 mb-1">Summary</h4>
                                  <p className="text-sm text-gray-600">{result.summary}</p>
                                </div>
                              )}

                              {/* Transcript Preview */}
                              <div className="rounded-md bg-white border border-gray-200 max-h-64 overflow-y-auto">
                                <div className="p-3 space-y-2">
                                  {result.segments.slice(0, 10).map((seg, idx) => (
                                    <div key={idx} className="text-sm">
                                      <span className="font-medium text-gray-900">{seg.speaker}</span>
                                      {seg.timestamp && (
                                        <span className="ml-2 text-xs text-gray-400">{seg.timestamp}</span>
                                      )}
                                      <p className="text-gray-600 mt-0.5">{seg.text}</p>
                                    </div>
                                  ))}
                                  {result.segments.length > 10 && (
                                    <p className="text-xs text-gray-400 text-center py-2">
                                      + {result.segments.length - 10} more segments
                                    </p>
                                  )}
                                </div>
                              </div>

                              {/* Actions */}
                              <div className="flex flex-wrap gap-2">
                                {result.itemId && (
                                  <Link
                                    to={`/transcript/${result.itemId}`}
                                    className="inline-flex items-center gap-1.5 rounded-md bg-blue-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-blue-700"
                                  >
                                    <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                      <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                      <path strokeLinecap="round" strokeLinejoin="round" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                                    </svg>
                                    View Full
                                  </Link>
                                )}
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleCopy(job.jobId, result);
                                  }}
                                  className="inline-flex items-center gap-1.5 rounded-md border border-gray-300 bg-white px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50"
                                >
                                  {copiedJobs.has(job.jobId) ? (
                                    <>
                                      <svg className="h-3.5 w-3.5 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                        <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                                      </svg>
                                      Copied!
                                    </>
                                  ) : (
                                    <>
                                      <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                        <path strokeLinecap="round" strokeLinejoin="round" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                                      </svg>
                                      Copy
                                    </>
                                  )}
                                </button>
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleDownload(result);
                                  }}
                                  className="inline-flex items-center gap-1.5 rounded-md border border-gray-300 bg-white px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50"
                                >
                                  <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                                  </svg>
                                  Download
                                </button>
                              </div>
                            </div>
                          )}
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </motion.div>
              );
            })}
          </AnimatePresence>
        </div>
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════
// SINGLE JOB VIEW (when jobId param is provided)
// ══════════════════════════════════════════════════════════════════════

function SingleJobView({ jobId }: { jobId: string }) {
  const navigate = useNavigate();
  const { loading: authLoading } = useAuth();
  const { jobs, addJob } = useTranscriptionJobs();
  const job = jobs.find((j) => j.jobId === jobId);

  // Other ongoing jobs (not the current one)
  const otherJobs = jobs.filter(j => j.jobId !== jobId && j.status === 'polling' && !j.hidden);

  // Local result state (only used for the rare completed-without-itemId fallback)
  const [title, setTitle] = useState('');
  const [channelName, setChannelName] = useState('');
  const [summary, setSummary] = useState('');
  const [segments, setSegments] = useState<Segment[]>([]);
  const [speakers, setSpeakers] = useState<ApiSpeaker[]>([]);
  const [durationMs, setDurationMs] = useState(0);
  const [minutesUsed, setMinutesUsed] = useState<number | undefined>();
  const [itemId, setItemId] = useState<string | undefined>();
  const [sourceUrl, setSourceUrl] = useState<string | undefined>();
  const [videoId, setVideoId] = useState<string | undefined>();
  const [resultFetched, setResultFetched] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [notFoundChecked, setNotFoundChecked] = useState(false);

  const bootstrapTriedRef = useRef(false);

  // Bootstrap: ensure context tracks this job
  useEffect(() => {
    if (jobId && !job && !bootstrapTriedRef.current) {
      bootstrapTriedRef.current = true;
      addJob(jobId, '');
    }
  }, [jobId, job, addJob]);

  // Scroll to top when page loads
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Process completed job result — auto-redirect to library for audio playback
  const processResult = useCallback((result: JobResult, response: JobResponse) => {
    const libraryItemId = result.item_id || result.saved_item_id || result.id || response.item_id;

    // Auto-redirect to library page for full audio player experience
    if (libraryItemId) {
      navigate(`/transcript/${libraryItemId}`, { replace: true });
      return;
    }

    // Fallback: show ResultView if no library item (shouldn't happen for logged-in users)
    const speakerNames: Record<string, string> = {};
    (result.speakers || []).forEach((s) => {
      const name = s.display_name || s.name;
      if (name) speakerNames[s.id] = name;
    });

    const rawSegments = ensureSegmentsArray(result.segments);
    const displaySegments: Segment[] = rawSegments.map((seg) => ({
      speaker: speakerNames[seg.speaker] || seg.speaker,
      text: seg.text,
      timestamp: msToTimestamp(seg.start_ms),
      start_ms: seg.start_ms,
      end_ms: seg.end_ms,
    }));

    const lastSeg = rawSegments[rawSegments.length - 1];
    const duration = result.duration_ms || lastSeg?.end_ms || 0;

    setTitle(result.title || 'Transcript');
    setChannelName(result.channel_name || '');
    setSummary(result.summary || '');
    setSegments(displaySegments);
    setSpeakers(result.speakers || []);
    setDurationMs(duration);
    setMinutesUsed(result.minutes_used ?? response.result?.minutes_used);
    setItemId(libraryItemId);
    setSourceUrl(result.url);
    setVideoId(result.video_id);
    // NOTE: setPageState removed — pageState is now derived from context job
  }, [navigate]);

  // Derive view state from context job
  const progress = job
    ? job.status === 'polling'
      ? computeDisplayProgress(job.startedAt, job.estimatedDurationSec, job.stage)
      : job.progress
    : 0;
  const status = job?.stage ?? 'Loading...';
  const pageState: 'loading' | 'polling' | 'completed' | 'failed' | 'not_found' =
    !job
      ? bootstrapTriedRef.current && notFoundChecked
        ? 'not_found'
        : 'loading'
      : job.status === 'completed'
        ? 'completed'
        : job.status === 'failed'
          ? 'failed'
          : 'polling';

  // Auto-redirect to library on completion (logged-in path)
  useEffect(() => {
    if (job?.status === 'completed' && job.resultItemId) {
      navigate(`/transcript/${job.resultItemId}`, { replace: true });
    }
  }, [job?.status, job?.resultItemId, navigate]);

  // Fallback: completed without resultItemId — fetch full result one-shot
  useEffect(() => {
    if (
      job?.status === 'completed' &&
      !job.resultItemId &&
      !resultFetched
    ) {
      setResultFetched(true);
      const token = localStorage.getItem('tempo_token');
      fetch(`${API_BASE}/job/${jobId}`, {
        headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}) },
      })
        .then((r) => r.json())
        .then((data: JobResponse) => {
          if (data.status === 'completed' && data.result) {
            processResult(data.result, data);
          }
        })
        .catch((err) => console.error('Fallback result fetch failed:', err));
    }
  }, [job?.status, job?.resultItemId, jobId, resultFetched, processResult]);

  // Failed job: surface error from context
  useEffect(() => {
    if (job?.status === 'failed' && !errorMessage) {
      setErrorMessage(job.error || 'Transcription failed');
    }
  }, [job?.status, job?.error, errorMessage]);

  // not_found path: bootstrap completed but job dropped from context → library fallback
  useEffect(() => {
    if (bootstrapTriedRef.current && !job && !notFoundChecked) {
      setNotFoundChecked(true);
      apiFetch<{ item?: { id: string } }>(`/library/items/by-job/${jobId}`)
        .then((res) => {
          if (res.item?.id) {
            navigate(`/transcript/${res.item.id}`, { replace: true });
          }
        })
        .catch(() => { /* fall through to not_found render */ });
    }
  }, [job, notFoundChecked, jobId, navigate]);

  const handleRetry = () => {
    setErrorMessage('');
    bootstrapTriedRef.current = false;
    setNotFoundChecked(false);
    setResultFetched(false);
  };

  const handleStartNew = () => {
    navigate('/');
  };

  // ── Render States ──

  // Loading state
  if (pageState === 'loading' || authLoading) {
    return (
      <div className="min-h-screen bg-white">
        <div className="mx-auto max-w-4xl px-4 py-12">
          <div className="flex flex-col items-center justify-center gap-4">
            <div className="h-8 w-8 animate-spin rounded-full border-4 border-gray-200 border-t-blue-600" />
            <p className="text-sm text-gray-500">Loading transcription...</p>
          </div>
        </div>
      </div>
    );
  }

  // Not found state
  if (pageState === 'not_found') {
    return (
      <div className="min-h-screen bg-white">
        <div className="mx-auto max-w-2xl px-4 py-16 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="space-y-4"
          >
            <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-gray-100">
              <svg
                className="h-8 w-8 text-gray-400"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={1.5}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M9.879 7.519c1.171-1.025 3.071-1.025 4.242 0 1.172 1.025 1.172 2.687 0 3.712-.203.179-.43.326-.67.442-.745.361-1.45.999-1.45 1.827v.75M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-9 5.25h.008v.008H12v-.008z"
                />
              </svg>
            </div>
            <h1 className="text-xl font-semibold text-gray-900">Transcription not found</h1>
            <p className="text-sm text-gray-500">
              This transcription may have expired or doesn't exist. Completed transcriptions are saved to your library.
            </p>
            <div className="flex justify-center gap-3 pt-4">
              <Link
                to="/library"
                className="rounded-md border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
              >
                View Library
              </Link>
              <Link
                to="/"
                className="rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700"
              >
                New Transcription
              </Link>
            </div>
          </motion.div>
        </div>
      </div>
    );
  }

  // Failed state
  if (pageState === 'failed') {
    return (
      <div className="min-h-screen bg-white">
        <div className="mx-auto max-w-2xl px-4 py-16 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="space-y-4"
          >
            <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-red-100">
              <svg
                className="h-8 w-8 text-red-500"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={1.5}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z"
                />
              </svg>
            </div>
            <h1 className="text-xl font-semibold text-gray-900">Transcription failed</h1>
            <p className="text-sm text-gray-600 max-w-md mx-auto">{errorMessage}</p>
            <div className="flex justify-center gap-3 pt-4">
              <button
                onClick={handleRetry}
                className="rounded-md border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
              >
                Retry
              </button>
              <Link
                to="/"
                className="rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700"
              >
                Try Different URL
              </Link>
            </div>
          </motion.div>
        </div>
      </div>
    );
  }

  // Polling/Processing state
  if (pageState === 'polling') {
    return (
      <div className="min-h-screen bg-white">
        <div className="mx-auto max-w-4xl px-4 py-3 md:py-8">
          {/* Back to all jobs */}
          <Link
            to="/transcription"
            className="inline-flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700 mb-4"
          >
            <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
            </svg>
            All Transcriptions
          </Link>

          <ProgressView
            progress={progress}
            status={status}
            title={title || undefined}
            channelName={channelName || undefined}
            isRetrying={status.toLowerCase().includes('retrying')}
            retryCount={(status.match(/(\d+)\/3/) || [])[1] ? Number(RegExp.$1) : 0}
          />

          {/* Other ongoing jobs */}
          {otherJobs.length > 0 && (
            <div className="mt-8 border-t border-gray-100 pt-6">
              <h3 className="text-sm font-medium text-gray-700 mb-3">
                Other transcriptions in progress
              </h3>
              <div className="space-y-2">
                {otherJobs.map((job) => (
                  <Link
                    key={job.jobId}
                    to={`/transcription/${job.jobId}`}
                    className="flex items-center gap-3 rounded-lg border border-gray-200 bg-gray-50 px-4 py-3 hover:bg-gray-100 transition-colors"
                  >
                    <div className="h-2 w-2 shrink-0 animate-pulse rounded-full bg-blue-500" />
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium text-gray-900 truncate">{job.title}</p>
                      <p className="text-xs text-gray-500">{job.stage}</p>
                    </div>
                    {(() => {
                      const pct = computeDisplayProgress(job.startedAt, job.estimatedDurationSec, job.stage);
                      return (
                        <div className="flex items-center gap-2 shrink-0">
                          <div className="h-1.5 w-16 overflow-hidden rounded-full bg-gray-200">
                            <div
                              className="h-full rounded-full bg-blue-500 transition-all"
                              style={{ width: `${pct}%` }}
                            />
                          </div>
                          <span className="text-xs tabular-nums text-gray-400 w-8">
                            {pct}%
                          </span>
                        </div>
                      );
                    })()}
                  </Link>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }

  // Completed state
  return (
    <div className="min-h-screen bg-white">
      <div className="mx-auto max-w-6xl px-4 py-8">
        {/* Back to all jobs */}
        <Link
          to="/transcription"
          className="inline-flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700 mb-4"
        >
          <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
          </svg>
          All Transcriptions
        </Link>

        <ResultView
          title={title}
          channelName={channelName}
          summary={summary}
          segments={segments}
          speakers={speakers}
          durationMs={durationMs}
          minutesUsed={minutesUsed}
          itemId={itemId}
          sourceUrl={sourceUrl}
          videoId={videoId}
          onStartNew={handleStartNew}
        />
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════
// MAIN COMPONENT - Routes to single or multi view
// ══════════════════════════════════════════════════════════════════════

export default function TranscriptionPage() {
  const { jobId } = useParams<{ jobId: string }>();

  // If jobId provided, show single job view
  // If no jobId, show all active jobs
  if (jobId) {
    return <SingleJobView jobId={jobId} />;
  }

  return <MultiJobView />;
}
